import React from 'react';
import { IncidentSeverity } from '../../types';

interface ChipConfig {
  label: string;
  classes: string;
}

/**
 * Severity chip config, mirroring the emerald/amber/rose pattern from
 * `ComplianceCard.getChipConfig` (fire drills). A parent seeing a report
 * about their own child is a sensitive moment — severity needs to be
 * legible at a glance without reading as alarmist, so MINOR reads as calm
 * (emerald), SIGNIFICANT as attention-needed (amber), and only NOTIFIABLE
 * (the statutory category under S.I. 575/2018) reads as serious (rose).
 */
export function getSeverityChipConfig(severity: IncidentSeverity): ChipConfig {
  switch (severity) {
    case IncidentSeverity.NOTIFIABLE:
      return { label: 'Notifiable', classes: 'bg-rose-100 text-rose-800' };
    case IncidentSeverity.SIGNIFICANT:
      return { label: 'Significant', classes: 'bg-amber-100 text-amber-800' };
    case IncidentSeverity.MINOR:
    default:
      return { label: 'Minor', classes: 'bg-emerald-100 text-emerald-800' };
  }
}

const IncidentSeverityChip: React.FC<{ severity: IncidentSeverity; testId?: string }> = ({
  severity,
  testId,
}) => {
  const chip = getSeverityChipConfig(severity);
  return (
    <span
      data-testid={testId ?? 'incident-severity-chip'}
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${chip.classes}`}
    >
      {chip.label}
    </span>
  );
};

export default IncidentSeverityChip;
